import React, { useState, useEffect, useContext } from "react";
import { 
    Grid,
    IconButton,
    Tooltip,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box
} from "@material-ui/core";
import MUIDataTable from "mui-datatables";
import {
    AddCircle,
    
} from '@material-ui/icons';
import AddOrganization from './AddOrganization';
import OrganizationDetail from './OrganizationDetail';
import axios from "axios";
import { AppContext } from '../../context/AppContext';
import { useSortContext } from "../../context/SortContext";
  
const Organizations = () => {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [data, setData] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const appCtx = useContext(AppContext);
    const sortContext = useSortContext();
    
    // Filter states
    const [approvedFilter, setApprovedFilter] = useState('yes');
    const [shouldMergeFilter, setShouldMergeFilter] = useState('all');
    
    // Local sort state - initialize as null/empty to indicate no sorting on first load
    const [orderBy, setOrderBy] = useState(sortContext.organizationSort.name || null);
    const [order, setOrder] = useState(sortContext.organizationSort.direction || null);

    const fetchData = async () => {
        setLoading(true);

        axios.get('organizations').catch((e) => {
            setLoading(false);
            console.log(`failed to get orgs with error`, e);
            appCtx.setAlert({ type: 'error', message: e?.data?.message ?? 'Failed to get organizations' });
        }).then(result => {
            if (result?.data?.response) {
                console.log('Organization data received:', result.data.response);
                setData(result.data.response);
    
                if (selectedOrg) {
                    const newOrg = result.data.response.find(o => o.id === selectedOrg.id);
                    if (newOrg) {
                        setSelectedOrg(newOrg);
                    }
                }
            } else {
                setData([]);
            }
    
            setLoading(false);
        });
        
    };

    useEffect(() => {
        fetchData();
    }, []);
    
    const toggleModal = () => {
        setAddModalOpen(!addModalOpen);
    };

    const toggleOrgDetail = () => {
        setSelectedOrg(undefined);
    };

    // Handle sort request
    const handleRequestSort = (property) => {
        // If clicking on the same column that is already sorted DESC, clear sorting
        if (orderBy === property && order === 'desc') {
            setOrder(null);
            setOrderBy(null);
            sortContext.updateOrganizationSort({
                name: null,
                direction: null
            });
            return;
        }
        
        const isAsc = orderBy === property && order === 'asc';
        const newDirection = isAsc ? 'desc' : 'asc';
        setOrder(newDirection);
        setOrderBy(property);
        
        // Update sort context
        sortContext.updateOrganizationSort({
            name: property,
            direction: newDirection
        });
    };

    // Sort function
    const sortData = (data, orderBy, order) => {
        if (!data || data.length === 0 || !orderBy || !order) return data || [];
        
        return [...data].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];
            
            // Handle missing values
            if (aValue === undefined) aValue = '';
            if (bValue === undefined) bValue = '';
            
            // Compare values based on type
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return order === 'asc' 
                    ? aValue.localeCompare(bValue) 
                    : bValue.localeCompare(aValue);
            }
            
            // For numeric comparison
            return order === 'asc' 
                ? (aValue < bValue ? -1 : 1) 
                : (bValue < aValue ? -1 : 1);
        });
    };

    // Apply filters to data
    const applyFilters = (data) => {
        if (!data) return [];
        
        return data.filter(org => {
            // Filter by approved status
            if (approvedFilter !== 'all') {
                const isApproved = org.approved === 1;
                if (approvedFilter === 'yes' && !isApproved) return false;
                if (approvedFilter === 'no' && isApproved) return false;
            }
            
            // Filter by shouldMerge status
            if (shouldMergeFilter !== 'all') {
                const shouldMerge = org.shouldMerge === 1;
                if (shouldMergeFilter === 'yes' && !shouldMerge) return false;
                if (shouldMergeFilter === 'no' && shouldMerge) return false;
            }
            
            return true;
        });
    };
    
    // Get sorted and filtered data
    const filteredData = applyFilters(data);
    const sortedData = sortData(filteredData, orderBy, order);

    return (
        <>
            <Grid container spacing={4} justify="center" alignItems="center">
                {loading && (
                    <Grid item>
                        <CircularProgress />
                    </Grid>
                )}
                {!loading && (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" gap={2}>
                                <FormControl variant="outlined" style={{ minWidth: 150 }}>
                                    <InputLabel id="approved-filter-label">Approved</InputLabel>
                                    <Select
                                        labelId="approved-filter-label"
                                        value={approvedFilter}
                                        onChange={(e) => setApprovedFilter(e.target.value)}
                                        label="Approved"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                <FormControl variant="outlined" style={{ minWidth: 150 }}>
                                    <InputLabel id="should-merge-filter-label">Should Merge</InputLabel>
                                    <Select
                                        labelId="should-merge-filter-label"
                                        value={shouldMergeFilter}
                                        onChange={(e) => setShouldMergeFilter(e.target.value)}
                                        label="Should Merge"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Tooltip title="Add Organization">
                                <IconButton onClick={toggleModal}>
                                    <AddCircle />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' && order ? order : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'approved'}
                                            direction={orderBy === 'approved' && order ? order : 'asc'}
                                            onClick={() => handleRequestSort('approved')}
                                        >
                                            Approved
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'shouldMerge'}
                                            direction={orderBy === 'shouldMerge' && order ? order : 'asc'}
                                            onClick={() => handleRequestSort('shouldMerge')}
                                        >
                                            Should Merge
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedData.map((org) => (
                                    <TableRow key={org.id} hover>
                                        <TableCell>
                                            <Button onClick={() => setSelectedOrg(org)}>
                                                {org.name}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button 
                                                style={{ color: org.approved === 1 ? 'green' : 'red' }} 
                                                onClick={() => setSelectedOrg(org)}>
                                                {org.approved === 1 ? 'Yes' : 'No'}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button 
                                                style={{ color: org.shouldMerge === 1 ? 'green' : 'red' }} 
                                                onClick={() => setSelectedOrg(org)}>
                                                {org.shouldMerge === 1 ? 'Yes' : 'No'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Grid>
                )}
            </Grid>

            <AddOrganization open={addModalOpen} toggle={toggleModal} reload={fetchData} />
            {selectedOrg && (
                <OrganizationDetail open={selectedOrg !== null} toggle={toggleOrgDetail} organization={selectedOrg} organizations={data} reload={fetchData} />
            )}
        </>
    );
};
export default Organizations;